import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { CareElementOperatorType } from "@/Flowsheets/Models/CareElementOperatorType";

import styles from "./CareElementTarget.module.scss";

interface ICareComponentProps {
    value: boolean | string | number | Date | null;
    operator: CareElementOperatorType;
}

/**
 * Component to display the target value of a care element.
 *
 * @component
 * @example
 * <CareElementTarget
 *     value={targetValue}
 *     operator={CareElementOperatorType.EqualTo}
 * />
 * @param {ICareComponentProps} props - The properties for the component.
 * @property {boolean | string | number | Date | null} props.value - The target value of the care element.
 * @property {CareElementOperatorType} props.operator - The operator type for the care element.
 * @returns
 */
function CareElementTarget({ value, operator }: Readonly<ICareComponentProps>): JSX.Element {
    const { getText } = useTranslation();

    const targetLabel = useMemo(
        () => CareElementGridHelper.getTargetLabel(operator, value),
        [operator, value]
    );

    return (
        <p className={styles.target}>{getText("TargetLabel")}: {targetLabel}</p>
    );
}

export { CareElementTarget };
